// ModernPreviewPanel — Modern Layout の docked プレビューパネル (中右ドック最上部に常設).
//
// 方針 (パネル配置調整 2026-06-08):
//   - Modern では「プレビュー窓」を floating トグルでなく **中右ドックの最上部
//     (レイヤーパネルの上) に常設パネル化** する。floating 用の絶対座標・ドラッグ・
//     × ボタン (= PreviewOverlay の chrome) は docked では不要。
//   - サムネ描画ロジック (= canvas 全体縮小プレビュー + 表示範囲枠 + クリック/ドラッグ
//     による scroll 移動) は Classic の `previewOverlay.ts` 純関数を **再利用** し
//     二重実装を避ける (= PreviewOverlay.tsx と同じ requestAnimationFrame コピー戦略)。
//   - Classic の floating `PreviewOverlay` はそのまま (Classic / Modern floating 共有)。
//     こちらは docked 専用の薄いパネルとして別実装にする (chrome を持たないため)。
//
// engine/protocol には触れない。canvasRef を drawImage source に縮小コピーするだけ。
//
// ## 見出し (<header>) の撤去 (2026-08-19)
//
// `<header>プレビュー</header>` を撤去した (ユーザー判断 2026-08-18、`ModernBrushPanel` の
// ヘッダ撤去 (307f54e) を他パネルへ広げる作業の一環)。パネル名自体は失われない:
// section 要素の `aria-label="プレビュー"` (支援技術) / `ModernPanelDragShell` のグリップ帯の
// `aria-label`/`title` (マウス hover) / `ModernPanelVisibilityMenu` (パネル名一覧) が引き
// 続き名前を提供する。中身が canvas 1 枚だけなので「何の canvas か」の可視の手がかりは
// 上記 3 経路のみになるが、指示どおり撤去する (先行事例: `ModernColorPanel`/
// `ModernBrushPanel` も見出し無し)。section は元々 `p-2` を持つため、見出し撤去後も
// 上端の余白は変えていない。

import { useMemo, useRef } from 'react';
import type { CanvasViewMapper, ViewportOffset } from '../../../infrastructure/drawing/viewport.js';
import { measureSlot } from '../../../infrastructure/drawing/viewport.js';
import {
  computePreviewThumbnailSize,
  previewClickToViewport,
  previewClickToViewportRotated,
} from '../classic/previewOverlay.js';
import { canvasBackgroundStyle } from '../shared/canvasBackgroundStyle.js';
import { usePreviewThumbnailLoop } from '../shared/usePreviewThumbnailLoop.js';

/** docked 表示でのサムネ最大辺 (px)。右ドック幅 (200px) に収まるよう抑える。 */
const DOCKED_THUMBNAIL_MAX_EDGE = 168;

export interface ModernPreviewPanelProps {
  /** Main 描画 canvas. drawImage source 用. mount 後に確定. */
  readonly canvasRef: React.RefObject<HTMLCanvasElement | null>;
  /**
   * Canvas 表示枠 (= `overflow: hidden` の `<main>`). `clientWidth/Height`
   * (= 表示範囲サイズの近似) を読むだけ — 2026-09-29 viewport transform 移行
   * (決定5) 以降、pan 位置は `viewportRef`/`onSetViewport` 経由になった。
   */
  readonly wrapperRef: React.RefObject<HTMLDivElement | null>;
  /**
   * 表示変換の窓口 (= `useRoomChrome.viewMapper`)。表示範囲枠 (回転中は傾いた四角形、
   * canvas-rotation 段取り 2) の描画とサムネ click の逆算に使う。
   */
  readonly viewMapper: CanvasViewMapper;
  /** pan オフセットの書き込み (= `useRoomChrome.setViewport`)。サムネ click/drag で呼ぶ。 */
  readonly onSetViewport: (next: ViewportOffset) => void;
  /** Main canvas の native px (= zoom 適用前). サムネ寸法計算に使う. */
  readonly canvasWidth: number;
  readonly canvasHeight: number;
  /** 現在の zoom (1..16). viewport 矩形計算 + click → scroll 変換に使う. */
  readonly zoom: number;
  /**
   * canvas が左右反転表示中か (= V キー / 下部反転トグル). engine は canvas 要素に
   * `scaleX(-zoom)` を当てて反転するため、同じ scrollLeft でも実際に見えている領域は
   * 左右ミラーになる. サムネ (drawImage) は真ピクセルのまま (CSP 流 / Classic 統一)
   * で、表示範囲枠と click→scroll の X だけを thumb 幅で水平反射して実視野に合わせる
   * (= `previewOverlay.ts` の flipX 引数へ渡すだけ).
   *
   * 注 (2026-06-12): 反転トグルボタン自体は ModernToolRail (zoom 近傍の view 操作)
   * へ移設した (= CSP のナビゲーター下部クラスタ型を避ける)。ここで `flipX` を受けて
   * ナビ枠/クリックを実視野に合わせるのは CSP 固有でなく単なる正しさなので維持する。
   */
  readonly flipX: boolean;
  /**
   * 2026-06-13 (feature/preview-perf): 「自分のローカル操作中か」を返す getter。
   * `true` のときサムネ再生成をサスペンドする (= 表示範囲枠だけは追従)。caller
   * (ModernLayout) が `chrome.isStrokeActive()` (= 自分の描画中) + パン中を合成する。
   * Modern はプレビュー常時表示なので、描画中に毎フレーム全面ダウンスケールしない
   * ことが特に重要 (= カーソルのフレーム予算を守る)。未指定なら常に false。
   */
  readonly isSuspended?: () => boolean;
  /**
   * 2026-09-28 (feature/preview-panel-background): 部屋の背景可視/背景色。メイン
   * canvas (`DrawingCanvas`) は engine が透明に描き、背景色は CSS の inline
   * `background-color` で敷く方式 (2026-06-20 canvas-background-color)。プレビューは
   * main canvas を `drawImage` で縮小コピーするだけなので CSS 背景は写らない —
   * サムネ canvas 自体にも同じ CSS 背景 (可視: `backgroundRgb` / 非可視:
   * `canvas-bg-checker` 市松) を当てて解消する (`../shared/canvasBackgroundStyle.js`、
   * DrawingCanvas / Classic PreviewOverlay と同じ helper)。caller (ModernLayout) が
   * `chrome.backgroundVisible` / `chrome.backgroundRgb` をそのまま渡す。未指定時は
   * DrawingCanvas 側の既定 (可視・白) と同じ挙動になる。
   */
  readonly backgroundVisible?: boolean;
  readonly backgroundRgb?: number;
}

export const ModernPreviewPanel = ({
  canvasRef,
  wrapperRef,
  viewMapper,
  onSetViewport,
  canvasWidth,
  canvasHeight,
  zoom,
  flipX,
  isSuspended,
  backgroundVisible,
  backgroundRgb,
}: ModernPreviewPanelProps): React.ReactNode => {
  // canvasWidth/Height が変わったときだけ再計算 (biome useExhaustiveDependencies 対応)。
  const thumb = useMemo(
    () => computePreviewThumbnailSize(canvasWidth, canvasHeight, DOCKED_THUMBNAIL_MAX_EDGE),
    [canvasWidth, canvasHeight],
  );
  const thumbCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const isDraggingRef = useRef<boolean>(false);

  // 2026-06-13 (feature/preview-perf): サムネ + 表示範囲枠の rAF ループを Classic/Modern
  // 共有フックへ括り出した (= PreviewOverlay.tsx と同一実装)。サムネ再生成は低レート
  // スロットル + 自分の操作中サスペンド、表示範囲枠は分離して安く追従する (詳細は
  // usePreviewThumbnailLoop)。Modern はプレビュー常時表示なので、毎フレーム全面ダウン
  // スケールを止めるこの抑制が特に効く。
  usePreviewThumbnailLoop({
    thumbCanvasRef,
    canvasRef,
    wrapperRef,
    viewMapper,
    thumb,
    isSuspended,
  });

  // サムネ click/drag → viewport pan. setPointerCapture でドラッグ追従。
  const moveViewportToClient = (clientX: number, clientY: number): void => {
    const thumbCanvas = thumbCanvasRef.current;
    const wrapper = wrapperRef.current;
    if (thumbCanvas === null || wrapper === null) return;
    const r = thumbCanvas.getBoundingClientRect();
    const t = viewMapper.getTransform();
    if (t.rotation !== 0) {
      // 回転中: サムネ click → 真の canvas 座標 → 枠中央へ (回転対応 clamp)。
      const slot = measureSlot(wrapper);
      onSetViewport(
        previewClickToViewportRotated(
          thumb,
          clientX - r.left,
          clientY - r.top,
          t,
          slot.slotWidth,
          slot.slotHeight,
        ),
      );
      return;
    }
    const next = previewClickToViewport(
      thumb,
      clientX - r.left,
      clientY - r.top,
      wrapper.clientWidth,
      wrapper.clientHeight,
      zoom,
      flipX,
      canvasWidth,
      canvasHeight,
    );
    onSetViewport(next);
  };

  // 2026-09-28 (feature/preview-panel-background): サムネ canvas にもメインと同じ
  // CSS 背景を当てる (DrawingCanvas / Classic PreviewOverlay と同じ helper、= 二重
  // 実装を避ける)。
  const bg = canvasBackgroundStyle(backgroundVisible, backgroundRgb);

  return (
    <section
      className="flex flex-col gap-1 rounded-lg border border-[var(--color-border)] bg-[var(--color-panel)] p-2"
      aria-label="プレビュー"
      data-manual="view.preview"
    >
      <div className="flex justify-center">
        <canvas
          ref={thumbCanvasRef}
          // backing store (width/height 属性) は rAF ループ内で dpr 倍に設定する
          // (= DPR 対応、renderPreviewThumbnail)。CSS サイズを style で論理 px
          // (thumb.width/height) に固定しておかないと、backing を物理ピクセルに
          // 広げた瞬間に要素が dpr 倍の見た目で表示されてしまうため。
          // 背景 (`bg.style`) は DrawingCanvas と同じ CSS 方式 (2026-09-28、
          // `canvasBackgroundStyle` 共有 helper): 可視時は backgroundColor、非可視時は
          // 下の className の `canvas-bg-checker` に任せる (= 画素へ焼き込まない).
          style={{
            touchAction: 'none',
            cursor: 'crosshair',
            width: thumb.width,
            height: thumb.height,
            ...bg.style,
          }}
          className={bg.extraClassName ?? undefined}
          aria-label="キャンバス全体プレビュー (クリックで表示位置移動)"
          onPointerDown={(e) => {
            if (e.button !== 0) return;
            e.preventDefault();
            isDraggingRef.current = true;
            e.currentTarget.setPointerCapture(e.pointerId);
            moveViewportToClient(e.clientX, e.clientY);
          }}
          onPointerMove={(e) => {
            if (!isDraggingRef.current) return;
            moveViewportToClient(e.clientX, e.clientY);
          }}
          onPointerUp={(e) => {
            isDraggingRef.current = false;
            try {
              e.currentTarget.releasePointerCapture(e.pointerId);
            } catch {
              // defensive
            }
          }}
          onPointerCancel={(e) => {
            isDraggingRef.current = false;
            try {
              e.currentTarget.releasePointerCapture(e.pointerId);
            } catch {
              // defensive
            }
          }}
        />
      </div>
      {/* 反転トグルはここ (ナビ窓下部) には置かない。CSP のナビゲーター下部
          反転/回転クラスタの指紋を避けるため、view 操作は ModernToolRail の zoom
          近傍にまとめた (2026-06-12)。ナビ窓は flipX を受けて枠/クリックを実視野に
          合わせる正常化のみ担う。 */}
    </section>
  );
};
